import { useIsFocused } from '@react-navigation/core'
import Color from 'color'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DataTable, { IDataTableProps } from 'react-data-table-component'
import { View } from 'react-native'
import { ProgressBar, Text } from 'react-native-paper'
import { IconSortDropDown } from '../../assets/icons'
import { theme } from '@linqpal/common-frontend'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { SearchBar } from '../atoms/SearchBar'
import { merge } from 'lodash'
import { SortingOrder } from '@linqpal/models/src/dictionaries/global'

const { light } = theme

export const PAGE_SIZE = 25

interface ApiTableProps extends Omit<IDataTableProps, 'data'> {
  defaultSortColumn?: string
  showOverflow?: boolean
  CustomFilterComponent?: React.FC<{
    value: any
    store: any
    onChange: (arg: any) => void
  }>
  filterPlaceholder?: string
  apiCaller?: any
  extraApiParams?: any
  showFilters?: boolean
  showPagination?: boolean
  store?: any
  testID?: string
  onItemPress?: any
  searchable?: string | boolean | React.ReactNode | React.ReactNode[]
  data?: any[]
  count?: number
  loading?: boolean
  onChangePage?: (page: number) => void
  dataParam?: string
  onReload?: (arg: any[]) => void
  onSort?: (column: any, sortDirection: any) => void
  sortCol?: string
  sortDir?: string
  setSortColDir?: (arg: [string, string]) => void
  disableTable?: boolean
  emptyResult?: React.ReactElement
  sortIcon?: (sortDirection: SortingOrder | null) => JSX.Element
  className?: string
  page?: number
}

interface State {
  items?: any[]
  count?: number
  loading?: boolean
  errorMessage?: string
}

function ApiTable(props: ApiTableProps) {
  const { t } = useTranslation('global')
  const [
    { items = [], count = 0, loading = false, errorMessage = '' },
    setData,
  ] = useState<State>({})
  const [search, setSearch] = useState('')
  const [filterParams, setFilterParams] = useState({})
  const [[sortColumn, sortDirection], setSort] = useState([
    props.defaultSortColumn || '',
    'desc',
  ])
  const [page, setPage] = useState(props.page ?? 1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const request = useRef<null | (Promise<any> & { cancel: () => void })>(null)

  const isFocused = useIsFocused()
  const sortCol = useMemo(
    () => props.sortCol || sortColumn,
    [sortColumn, props.sortCol],
  )
  const sortDir = useMemo(
    () => props.sortDir || sortDirection,
    [sortDirection, props.sortDir],
  )

  const {
    style = {},
    title = '',
    noTableHead = false,
    disabled,
    CustomFilterComponent: Filter,
    filterPlaceholder = '',
    apiCaller,
    showOverflow,
    expandableRows = false,
    expandableRowsComponent,
    expandableRowDisabled,
    expandableRowExpanded,
    extraApiParams = null,
    conditionalRowStyles = [],
    showFilters = true,
    showPagination = true,
    searchable = true,
    store = null,
    testID = `api_table_${new Date().getTime()}`,
    dataParam = 'items',
    onReload,
    setSortColDir,
    disableTable,
    className,
    sortIcon,
    onSort,
  } = props

  const callAPI = useCallback(
    () => {
      request.current?.cancel()

      if (!apiCaller) return

      setData({ loading: true })
      request.current = apiCaller({
        ...extraApiParams,
        ...filterParams,
        search,
        sortColumn: sortCol,
        sortDirection: sortDir,
        page,
        pageSize,
      })

      request.current
        ?.then((data) => {
          const d = {
            ...data,
            items: data.items || [],
            count: data.count || data.totalCount || data.items?.length || 0,
          }
          if (store) store.setData(d)
          setData(d)
          if (onReload) onReload(data.items || [])
        })
        .catch((e) => {
          if (e.code !== 'request-cancelled') {
            const message = e.data?.message || e.message || e.toString()
            setData({ errorMessage: message })
          }
        })
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      request,
      apiCaller,
      extraApiParams,
      filterParams,
      search,
      sortCol,
      sortDir,
      page,
      pageSize,
      store,
    ],
  )

  const apiFetch = !disabled && isFocused && !props.data ? callAPI : null

  useEffect(() => {
    apiFetch && apiFetch()
    return () => {
      request.current?.cancel()
    }
  }, [apiFetch])

  const filters = useMemo(
    () =>
      Filter ? (
        <Filter value={filterParams} onChange={setFilterParams} store={store} />
      ) : null,
    [Filter, filterParams, store],
  )

  const filterComponent = useMemo(
    () => (
      <FilterComponent
        search={search}
        onSearch={setSearch}
        placeholder={filterPlaceholder}
        filters={filters}
        testID={`${testID}_filter_component`}
      />
    ),
    [search, filterPlaceholder, filters, testID],
  )

  const noDataText = useMemo(
    () =>
      errorMessage ? (
        <Text style={{ color: light.colors.error }} testID={errorMessage}>
          {errorMessage}
        </Text>
      ) : (
        <Text testID="no_items">{t('noItems')}</Text>
      ),
    [errorMessage, t],
  )
  const noData = props.emptyResult || noDataText

  return (
    <DataTable
      className={className}
      style={composeStyle(style, showOverflow && { overflow: 'visible' })}
      onRowClicked={props.onItemPress}
      noHeader={!title}
      disabled={disableTable}
      title={title}
      noTableHead={noTableHead}
      noContextMenu
      subHeader={searchable}
      subHeaderComponent={showFilters ? filterComponent : null}
      subHeaderAlign="left"
      columns={props.columns}
      data={
        props.data
          ? props.data.slice()
          : store
          ? store[dataParam].slice()
          : items
      }
      sortServer
      defaultSortField={sortCol}
      defaultSortAsc={sortDir !== 'desc'}
      onSort={useCallback(
        (column, dir) => {
          const { id, name, selector } = column
          const col =
            typeof selector === 'string'
              ? selector
              : typeof name === 'string'
              ? name
              : typeof id === 'string'
              ? id
              : '_id'
          if (onSort) {
            onSort(col, dir)
          }
          if (setSortColDir) {
            setSortColDir([col, dir])
            setSort([col, dir])
          } else setSort([col, dir])
        },
        [setSortColDir, onSort],
      )}
      pagination={showPagination}
      paginationServer
      paginationDefaultPage={page}
      paginationPerPage={pageSize}
      paginationTotalRows={props.count ?? count}
      paginationRowsPerPageOptions={useMemo(() => [PAGE_SIZE], [])}
      onChangePage={useCallback(
        (p) => {
          props.onChangePage?.(p)
          setPage(p)
        },
        [props],
      )}
      onChangeRowsPerPage={useCallback((ps) => setPageSize(ps), [])}
      progressPending={props.loading ?? loading}
      progressComponent={useMemo(
        () =>
          props.progressComponent ?? (
            <ProgressBar
              indeterminate
              visible={props.loading ?? loading}
              testID="progress_bar"
              color={theme.commonColors.accentText}
            />
          ),
        [loading, props.loading, props.progressComponent],
      )}
      noDataComponent={noData}
      highlightOnHover
      // lodash.merge mutates an underlying object
      // so there must be an empty object as first parameter
      // otherwise custom style of one table may affect others
      customStyles={merge({}, customStyles, props.style)}
      conditionalRowStyles={conditionalRowStyles}
      sortIcon={useMemo(
        () => (
          <View testID="sort_icon">
            {sortIcon ? (
              sortIcon(sortColumn ? (sortDir as SortingOrder) : null)
            ) : (
              <IconSortDropDown />
            )}
          </View>
        ),
        [sortIcon, sortDir, sortColumn],
      )}
      keyField={props.keyField}
      expandableRowsHideExpander
      expandOnRowClicked
      expandableRows={expandableRows}
      expandableRowsComponent={expandableRows ? expandableRowsComponent : false}
      expandableRowDisabled={expandableRowDisabled}
      expandableRowExpanded={expandableRowExpanded}
    />
  )
}

export default observer(ApiTable)

const FilterComponent = React.memo<{
  search: string
  placeholder: string
  onSearch(value: string): any
  testID: string
  filters?: any
}>(function FilterComponent(props) {
  const placeholder = props.placeholder || 'Business name, account number...'

  return (
    <View style={{ width: '100%', flexDirection: 'row', flexWrap: 'wrap' }}>
      <SearchBar
        value={props.search}
        onChange={props.onSearch}
        placeholder={placeholder}
      />
      {props.filters}
    </View>
  )
})

const customStyles = {
  table: {
    style: { backgroundColor: light.colors.surface },
  },
  tableWrapper: {
    style: { backgroundColor: light.colors.surface },
  },
  header: {
    style: { paddingLeft: 0, paddingRight: 0, minHeight: undefined },
  },
  subHeader: {
    style: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 32,
    },
  },
  head: {
    style: {},
  },
  headRow: {
    style: {
      borderBottomColor: Color(light.colors.borderLight)
        .alpha(0.6)
        .rgb()
        .string(),
      minHeight: 48,
    },
    denseStyle: {},
  },
  headCells: {
    style: {
      color: light.colors.primary,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 'bold',
    },
    activeSortStyle: {
      color: light.colors.primary,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 'bold',
      width: '100%',

      div: {
        width: '100%',
      },
    },
    inactiveSortStyle: {
      color: light.colors.primary,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 'bold',
      width: '100%',

      div: {
        width: '100%',
      },
      span: {
        textDecoration: 'none',
        div: {
          svg: {
            opacity: '0.2 !important',
          },
        },
      },
    },
  },
  contextMenu: {
    style: {},
    activeStyle: {},
  },
  cells: {
    style: {
      color: light.colors.primary,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: '400',
    },
  },
  rows: {
    style: {
      borderBottomColor: Color(light.colors.borderLight)
        .alpha(0.6)
        .rgb()
        .string(),
    },
    selectedHighlightStyle: {},
    denseStyle: {},
    highlightOnHoverStyle: {
      backgroundColor: Color(light.colors.accentText)
        .alpha(0.08)
        .rgb()
        .string(),
      borderBottomColor: Color(light.colors.borderLight)
        .alpha(0.6)
        .rgb()
        .string(),
      outlineColor: Color(light.colors.borderLight).alpha(0.6).rgb().string(),
    },
    stripedStyle: {},
  },
  expanderRow: {
    style: {},
  },
  expanderCell: {
    style: {},
  },
  expanderButton: {
    style: {},
  },
  pagination: {
    style: {},
    pageButtonsStyle: {},
  },
  noData: {
    style: {},
  },
  progress: {
    style: { width: '100%' },
  },
}

// eventually this style will be used for all tables
// meanwhile keep it here
export const tableStyles = {
  header: {
    height: 54,
  },
  headRow: {
    borderBottomColor: '#E6E8EA',
    border: '1px',
  },
  headCells: {
    style: {
      paddingLeft: 2,
      paddingRight: 2,
      paddingBottom: 8,
      fontSize: 12,
      lineHeight: 22,
      fontFamily: 'Inter, sans-serif',
      fontWeight: '700',
      backgroundColor: '#F8F9F9',
      color: '#19262F',
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'flex-start',
      flexDirection: 'row',
    },
    activeSortStyle: {
      fontSize: 12,
      transform: 'none',
      width: 'auto',
      gap: '7px',
      div: {
        svg: {
          transform: 'none !important',
        },
      },
    },
    inactiveSortStyle: {
      fontSize: 12,
      width: 'auto',
      gap: '7px',
      span: {
        textDecoration: 'none',
        div: {
          svg: {
            transform: 'none !important',
            opacity: '0.5 !important',
            fill: '#99ADBA',
          },
        },
      },
    },
  },
  cells: {
    style: {
      fontSize: 14,
      alignItems: 'center',
      justifyContent: 'flex-start',
      paddingLeft: 2,
      paddingRight: 2,
      height: 68,
      color: '#19262F',
      gap: 4,
    },
  },
}
